import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtTRY, fmtDate } from "../lib/format";
import { PageHeader } from "../components/Common";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash, Bank, X, PencilSimple, Car } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Financing() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get("/financings").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/vehicles").then(({ data }) => setVehicles(data));
  }, []);

  const plateOf = (id) => {
    const v = vehicles.find((x) => x.id === id);
    return v ? v.plate : null;
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", lender: "", principal: "", monthly_installment: "", total_installments: "", paid_installments: 0, interest_rate: "", start_date: new Date().toISOString().slice(0, 10), vehicle_ids: [] });
    setModal(true);
  };
  const openEdit = (f) => {
    setEditing(f);
    setForm({ ...f, vehicle_ids: f.vehicle_ids || [] });
    setModal(true);
  };

  const toggleVehicle = (id) => {
    setForm((prev) => {
      const ids = prev.vehicle_ids || [];
      return { ...prev, vehicle_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, lender: form.lender, start_date: form.start_date,
      principal: parseFloat(form.principal),
      monthly_installment: parseFloat(form.monthly_installment),
      total_installments: parseInt(form.total_installments),
      paid_installments: parseInt(form.paid_installments) || 0,
      interest_rate: parseFloat(form.interest_rate) || 0,
      vehicle_ids: form.vehicle_ids || [],
    };
    try {
      if (editing) await api.put(`/financings/${editing.id}`, payload);
      else await api.post("/financings", payload);
      toast.success(editing ? "Finansman güncellendi" : "Finansman eklendi");
      setModal(false);
      load();
    } catch { toast.error("İşlem başarısız"); }
  };

  const remove = async (id) => { if (!window.confirm("Bu finansman kaydını silmek istediğinize emin misiniz?")) return; await api.delete(`/financings/${id}`); toast.success("Silindi"); load(); };

  const totalRemaining = items.reduce((s, f) => s + f.monthly_installment * (f.total_installments - f.paid_installments), 0);
  const totalMonthly = items.reduce((s, f) => s + (f.paid_installments < f.total_installments ? f.monthly_installment : 0), 0);

  return (
    <div className="p-8 max-w-[1400px]">
      <PageHeader title="Finansman" subtitle="Araç kredileri ve finansal kiralama takibi" action={
        user?.role === "admin" && (
          <button onClick={openNew} data-testid="add-financing-button" className="bg-zinc-950 text-white rounded-sm px-4 py-2.5 text-sm font-semibold hover:bg-zinc-800 transition-colors flex items-center gap-2">
            <Plus size={16} weight="bold" /> Kredi Ekle
          </button>
        )
      } />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 animate-fade-up">
        <Card label="Aktif Kredi Sayısı" value={items.filter(f => f.paid_installments < f.total_installments).length} />
        <Card label="Toplam Kalan Borç" value={fmtTRY(totalRemaining)} cls="text-[#002FA7]" />
        <Card label="Aylık Toplam Taksit" value={fmtTRY(totalMonthly)} cls="text-red-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-fade-up">
        {items.map((f) => {
          const remaining = f.total_installments - f.paid_installments;
          const pct = Math.round((f.paid_installments / f.total_installments) * 100);
          const plates = (f.vehicle_ids || []).map(plateOf).filter(Boolean);
          return (
            <div key={f.id} className="bg-white border border-zinc-200 rounded-sm p-6 hover:-translate-y-px hover:shadow-sm transition-all duration-200" data-testid={`financing-${f.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-zinc-100 rounded-sm flex items-center justify-center"><Bank size={18} weight="bold" className="text-[#002FA7]" /></div>
                  <div>
                    <div className="font-semibold text-zinc-900">{f.name}</div>
                    <div className="text-xs text-zinc-500">{f.lender} · %{f.interest_rate} faiz · {fmtDate(f.start_date)}</div>
                  </div>
                </div>
                {user?.role === "admin" && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(f)} data-testid={`edit-financing-${f.id}`} className="p-1.5 text-zinc-400 hover:text-[#002FA7] transition-colors"><PencilSimple size={16} /></button>
                    <button onClick={() => remove(f.id)} data-testid={`delete-financing-${f.id}`} className="p-1.5 text-zinc-400 hover:text-red-600 transition-colors"><Trash size={16} /></button>
                  </div>
                )}
              </div>

              {plates.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid={`financing-vehicles-${f.id}`}>
                  <Car size={13} className="text-zinc-400" />
                  {plates.map((p) => <span key={p} className="text-[11px] font-mono font-semibold text-zinc-700 bg-zinc-100 rounded-sm px-1.5 py-0.5">{p}</span>)}
                </div>
              )}

              <div className="mb-3">
                <div className="flex justify-between text-xs text-zinc-500 mb-1.5">
                  <span>{f.paid_installments}/{f.total_installments} taksit ödendi</span>
                  <span className="font-mono">%{pct}</span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#002FA7] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 pt-3 border-t border-zinc-100">
                <Stat label="Anapara" value={fmtTRY(f.principal)} />
                <Stat label="Aylık Taksit" value={fmtTRY(f.monthly_installment)} />
                <Stat label="Kalan Borç" value={fmtTRY(f.monthly_installment * remaining)} accent />
              </div>
            </div>
          );
        })}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-sm border border-zinc-200 w-full max-w-md p-6 animate-fade-up max-h-[90vh] overflow-y-auto" data-testid="financing-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl font-semibold">{editing ? "Finansmanı Düzenle" : "Yeni Finansman"}</h3>
              <button type="button" onClick={() => setModal(false)}><X size={20} className="text-zinc-400 hover:text-zinc-900" /></button>
            </div>
            <div className="space-y-4">
              <F label="Kredi Adı"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fin-name" className="inp" placeholder="Filo Genişleme Kredisi" /></F>
              <F label="Kredi Veren"><input required value={form.lender} onChange={(e) => setForm({ ...form, lender: e.target.value })} data-testid="fin-lender" className="inp" placeholder="Ziraat Bankası" /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Anapara (₺)"><input type="number" required value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} data-testid="fin-principal" className="inp font-mono" /></F>
                <F label="Faiz (%)"><input type="number" step="0.01" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} data-testid="fin-rate" className="inp font-mono" /></F>
              </div>
              <F label="Aylık Taksit (₺)"><input type="number" required value={form.monthly_installment} onChange={(e) => setForm({ ...form, monthly_installment: e.target.value })} data-testid="fin-installment" className="inp font-mono" /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Toplam Taksit"><input type="number" required value={form.total_installments} onChange={(e) => setForm({ ...form, total_installments: e.target.value })} data-testid="fin-total" className="inp font-mono" /></F>
                <F label="Ödenen Taksit"><input type="number" value={form.paid_installments} onChange={(e) => setForm({ ...form, paid_installments: e.target.value })} data-testid="fin-paid" className="inp font-mono" /></F>
              </div>
              <F label="Başlangıç"><input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="fin-date" className="inp" /></F>
              <F label="Bağlı Araçlar (bu kredi hangi araçlar için?)">
                <div className="border border-zinc-200 rounded-sm max-h-40 overflow-y-auto divide-y divide-zinc-100" data-testid="fin-vehicles">
                  {vehicles.length === 0 && <div className="px-3 py-2 text-xs text-zinc-400">Araç yok</div>}
                  {vehicles.map((v) => (
                    <label key={v.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-zinc-50">
                      <input type="checkbox" checked={(form.vehicle_ids || []).includes(v.id)} onChange={() => toggleVehicle(v.id)} data-testid={`fin-veh-${v.id}`} className="w-4 h-4 accent-[#002FA7]" />
                      <span className="font-mono font-semibold text-zinc-800">{v.plate}</span>
                      <span className="text-zinc-500 text-xs">{v.brand} {v.model}{v.status === "sold" ? " · Satıldı" : ""}</span>
                    </label>
                  ))}
                </div>
              </F>
            </div>
            <button type="submit" data-testid="fin-submit" className="w-full mt-6 bg-zinc-950 text-white rounded-sm py-2.5 text-sm font-semibold hover:bg-zinc-800 transition-colors">Kaydet</button>
          </form>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, cls }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6">
      <span className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500">{label}</span>
      <div className={`font-mono text-2xl font-semibold mt-3 tabular-nums ${cls || "text-zinc-950"}`}>{value}</div>
    </div>
  );
}
function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{label}</div>
      <div className={`font-mono text-sm font-semibold mt-0.5 tabular-nums ${accent ? "text-[#002FA7]" : "text-zinc-800"}`}>{value}</div>
    </div>
  );
}
function F({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
